import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Instagram } from "lucide-react";
import { motion } from "framer-motion";
import { ESSAYS, SOCIALS, TAGS } from "../data/mock";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ShareMenu } from "../components/ShareMenu";
import { EngageCard } from "../components/EngageCard";
import { useSEO } from "../hooks/useSEO";

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2>{block.text}</h2>;
    case "h3":
      return <h3>{block.text}</h3>;
    case "blockquote":
      return <blockquote>{block.text}</blockquote>;
    case "callout":
      return <div className="callout">{block.text}</div>;
    case "list":
      return (
        <ul>
          {block.items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      );
    case "image":
      return (
        <figure className="my-10 -mx-2 md:mx-0">
          <img
            src={block.src}
            alt={block.alt || ""}
            loading="lazy"
            className="w-full rounded-2xl"
            style={{ border: "1px solid var(--brand-line)" }}
          />
          {block.caption && (
            <figcaption
              className="mt-3 text-[13px] text-center italic"
              style={{ color: "var(--brand-muted)" }}
            >
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case "video":
      return (
        <figure className="my-10 -mx-2 md:mx-0">
          <div
            className="relative w-full aspect-video overflow-hidden rounded-2xl"
            style={{ border: "1px solid var(--brand-line)" }}
          >
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${block.id}?rel=0&modestbranding=1`}
              title={block.title || "Embedded video"}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
          {block.title && (
            <figcaption
              className="mt-3 text-[13px] text-center italic"
              style={{ color: "var(--brand-muted)" }}
            >
              {block.title}
            </figcaption>
          )}
        </figure>
      );
    default:
      return <p>{block.text}</p>;
  }
};

export const EssayDetail = () => {
  const { slug } = useParams();
  const essay = ESSAYS.find((e) => e.slug === slug);

  const wordCount = essay
    ? essay.body.reduce((sum, b) => {
        const text = b.text || (b.items ? b.items.join(" ") : "");
        return sum + text.split(/\s+/).filter(Boolean).length;
      }, 0)
    : 0;

  useSEO({
    title: essay ? `${essay.title}, Hunar Syal` : "Essay not found",
    description: essay?.subtitle,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    jsonLd: essay
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: essay.title,
          description: essay.subtitle,
          author: {
            "@type": "Person",
            name: "Hunar Syal",
            url: "https://hunarsyal.com",
          },
          publisher: {
            "@type": "Person",
            name: "Hunar Syal",
          },
          datePublished: essay.date,
          articleSection: essay.category,
          wordCount,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              typeof window !== "undefined" ? window.location.href : undefined,
          },
        }
      : undefined,
  });

  if (!essay) return <Navigate to="/" replace />;

  const related = ESSAYS.filter((e) => e.id !== essay.id).slice(0, 3);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div data-testid="essay-detail-page" style={{ background: "var(--brand-cream)" }}>
      <Navbar />

      <article className="pt-28 md:pt-36 pb-20 md:pb-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-14">
          <div className="min-w-0 max-w-2xl mx-auto lg:mx-0">
          <Link
            to="/#essays"
            data-testid="essay-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold mb-8 hover:opacity-70 transition-opacity"
            style={{ color: "var(--brand-muted)" }}
          >
            <ArrowLeft size={15} strokeWidth={2} />
            Back to essays
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className="flex flex-wrap items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.22em]"
              style={{ color: "var(--brand-accent)" }}
            >
              <span>{essay.category}</span>
              <span className="w-1 h-1 rounded-full" style={{ background: "var(--brand-muted)" }} />
              <span style={{ color: "var(--brand-muted)" }}>{essay.date}</span>
              <span className="w-1 h-1 rounded-full" style={{ background: "var(--brand-muted)" }} />
              <span style={{ color: "var(--brand-muted)" }}>{essay.readTime}</span>
            </div>

            <h1
              className="mt-6 text-4xl md:text-[54px] font-bold tracking-tight leading-[1.08]"
              style={{ color: "var(--brand-ink)" }}
              data-testid="essay-title"
            >
              {essay.title}
            </h1>

            <p
              className="mt-5 text-lg md:text-xl leading-snug"
              style={{ color: "var(--brand-muted)" }}
            >
              {essay.subtitle}
            </p>

            <div
              className="mt-8 pt-6 flex items-center justify-between gap-4"
              style={{ borderTop: "1px solid var(--brand-line)" }}
            >
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--brand-ink)" }}>
                  Hunar Syal
                </p>
                <p className="text-xs mt-0.5" style={{ color: "var(--brand-muted)" }}>
                  Writing on mindset, money & careers
                </p>
              </div>
              <ShareMenu
                title={essay.title}
                text={essay.subtitle}
                url={shareUrl}
                testIdPrefix="essay-share"
              />
            </div>
          </motion.div>

          <div className="article-body mt-12 md:mt-14" data-testid="essay-body">
            {essay.body.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </div>

          {essay.tags?.length > 0 && (
            <div
              className="mt-14 pt-8"
              style={{ borderTop: "1px solid var(--brand-line)" }}
              data-testid="essay-tags"
            >
              <p
                className="text-[11px] font-semibold uppercase tracking-[0.28em] mb-3"
                style={{ color: "var(--brand-accent)" }}
              >
                Tagged in
              </p>
              <div className="flex flex-wrap gap-2">
                {essay.tags.map((t) => {
                  const tag = TAGS.find((x) => x.slug === t);
                  if (!tag) return null;
                  return (
                    <Link
                      key={t}
                      to={`/tags/${t}`}
                      data-testid={`essay-detail-tag-${t}`}
                      className="text-sm font-semibold px-4 py-2 rounded-full transition-transform duration-300 hover:-translate-y-0.5"
                      style={{
                        background: "var(--brand-cream-soft)",
                        border: "1px solid var(--brand-line)",
                        color: "var(--brand-ink-soft)",
                      }}
                    >
                      #{tag.label.toLowerCase()}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          <div
            className="mt-16 pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            style={{ borderTop: "1px solid var(--brand-line)" }}
          >
            <div>
              <p className="text-lg font-bold" style={{ color: "var(--brand-ink)" }}>
                Enjoyed this article?
              </p>
              <p className="text-sm mt-1" style={{ color: "var(--brand-muted)" }}>
                Tell me what landed. I read every message.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={SOCIALS.instagramDM}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="essay-get-in-touch"
                className="inline-flex items-center gap-2 rounded-full btn-primary font-semibold px-6 py-3"
              >
                <Instagram size={17} strokeWidth={2} />
                Get in touch
              </a>
              <ShareMenu
                title={essay.title}
                text={essay.subtitle}
                url={shareUrl}
                testIdPrefix="essay-share-bottom"
              />
            </div>
          </div>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-28">
              <EngageCard />
            </div>
          </div>

          <div className="lg:hidden mt-12 max-w-2xl mx-auto">
            <EngageCard />
          </div>
        </div>
      </article>

      <section
        className="py-20 px-6 md:px-12 lg:px-20"
        style={{
          background: "var(--brand-cream-soft)",
          borderTop: "1px solid var(--brand-line)",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <h3
            className="text-3xl md:text-4xl font-bold tracking-tight mb-10"
            style={{ color: "var(--brand-ink)" }}
          >
            Keep reading
          </h3>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/essays/${r.slug}`}
                data-testid={`essay-related-${r.id}`}
                className="group block rounded-[16px] p-6 transition-transform duration-300 hover:-translate-y-1"
                style={{
                  background: "var(--brand-cream)",
                  border: "1px solid var(--brand-line)",
                }}
              >
                <span
                  className="text-[11px] font-semibold uppercase tracking-[0.2em]"
                  style={{ color: "var(--brand-accent)" }}
                >
                  {r.category}
                </span>
                <h4
                  className="mt-3 text-xl font-bold tracking-tight leading-snug"
                  style={{ color: "var(--brand-ink)" }}
                >
                  {r.title}
                </h4>
                <p
                  className="mt-2 text-sm line-clamp-2"
                  style={{ color: "var(--brand-muted)" }}
                >
                  {r.subtitle}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EssayDetail;
